import { useToast } from '@afo/bsf-core'
import type { ISelectOption } from '@afo/bsf-core/types'
import { EUIFormVariant, EUiFormCountry } from '@/domains/direct-sales/const/form.const'
import { PRODUCT_NAME } from '@/domains/direct-sales/const/product.const'
import type { Product } from '@/domains/direct-sales/const/product.const'
import type { IProps, IUiFormProducts } from '@/domains/direct-sales/types/form.types'
import useUiForm from '@/domains/direct-sales/composables/useUiForm'
import useDirectSalesApi from '@/domains/direct-sales/api/useDirectSalesApi'
import { useRecaptcha } from '@/domains/recaptcha/composables/useRecaptcha'
import { useUtm } from '@/domains/direct-sales/composables/useUtm'

export interface IUseDirectSalesFormOptions {
  fallbackRedirectTo?: string
}

type TDirectSalesFormProps = Required<Pick<IProps, 'redirectTo' | 'variant' | 'utm'>> & Pick<IProps, 'product'>

export function useDirectSalesForm(
  props: TDirectSalesFormProps,
  options: IUseDirectSalesFormOptions = {},
) {
  const router = useRouter()
  const localePath = useLocalePath()
  const config = useRuntimeConfig().public
  const { locale, t } = useI18n()
  const route = useRoute()
  const utms = useUtm(props.utm)
  const { $v, form, consentForm, consents, setAllConsents } = useUiForm(props.variant)
  const { submitLead } = useDirectSalesApi()
  const { recaptchaValid } = useRecaptcha()
  const toast = useToast()
  const expand = ref<boolean>(false)

  const isDisabled = ref(false)

  const isProducts = computed(() => props.variant === EUIFormVariant.Products)
  const productForm = form as IUiFormProducts

  const productList = computed(() => {
    if (isProducts.value) {
      return productForm.products
    }

    if (props.product) {
      return [props.product as string]
    }

    const chosenProducts = route.query.chosen_products?.toString() ?? ''
    return chosenProducts ? chosenProducts.split(';') : []
  })

  const allConsentsChecked = computed(() =>
    consents.every(({ key }) => consentForm[key].value),
  )

  const countryOptions = computed<ISelectOption[]>(() => [
    { value: EUiFormCountry.POLAND, text: t('form.country.poland') },
    { value: EUiFormCountry.OTHER, text: t('form.country.other') },
  ])

  const variantPayload = (): Record<string, unknown> => ({
    ...utms,
    chosenProducts: productList.value.join(';'),
  })

  async function submitForm() {
    isDisabled.value = true

    if (recaptchaValid.value) {
      isDisabled.value = false
      return
    }

    const isValid = await $v.value.$validate()
    if (!isValid) {
      isDisabled.value = false
      return
    }

    try {
      const { firstName, lastName, email, phone, companyName, nip, description, companySize } = form
      const formId = crypto.randomUUID()

      const data = {
        firstName,
        lastName,
        email,
        companyName,
        phone,
        nip,
        analyticsUuid: formId,
        ...(description.trim() ? { message: description } : {}),
        companySize: companySize.value,
        dataProcessingConsent: +(consentForm.rodo.value ?? false),
        emailConsent: +(consentForm.email.value ?? false),
        phoneConsent: +(consentForm.phone.value ?? false),
        smsConsent: +(consentForm.phone.value ?? false),
        thirdPartyConsent: +(consentForm.partners.value ?? false),
        ...variantPayload(),
      }

      const response = await submitLead(data)

      productList.value.forEach((value) => {
        (window as Window & { dataLayer?: Record<string, unknown>[] }).dataLayer?.push({
          event: 'form_submit_button_clicked',
          product_name: PRODUCT_NAME[value as Product],
          company_size: companySize.value,
          lang: locale.value,
        })
      })

      if (response.status === 201) {
        (window as Window & { dataLayer?: Record<string, unknown>[] }).dataLayer?.push({
          event: 'form_sent',
          form_id: formId,
        })
        const routeToRedirect = props.redirectTo || options.fallbackRedirectTo || ''
        await router.push(localePath(routeToRedirect))
      } else {
        toast.error(t('error.directSales'))
      }
    } catch (error) {
      console.log(error)
      toast.error(t('error.directSales'))
    } finally {
      isDisabled.value = false
    }
  }

  return {
    config,
    $v,
    form,
    productForm,
    consentForm,
    consents,
    setAllConsents,
    isDisabled,
    isProducts,
    productList,
    allConsentsChecked,
    countryOptions,
    recaptchaValid,
    submitForm,
    expand,
  }
}

export default useDirectSalesForm
